import { CheckmarkFilled, Edit, WarningFilled } from "@carbon/icons-react";
import { Button, Checkbox, Search, Tag } from "@carbon/react";
import { FC, useMemo, useState } from "react";

import { ModalTransition } from "src/components/Modal";
import { PropertyValueInput } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useT } from "src/internationalization";
import {
  BlacklistElementModal,
  BlacklistElementModalItem,
} from "src/pages/Blacklists/BlacklistsTable/BlacklistElementModal";
import { BlacklistableCaseIdentifier, BlacklistedCaseIdentifier } from "src/types/case";
import { ClientSchemaBlacklist } from "src/types/clientSchema";
import { Property } from "src/types/property";
import { cn } from "src/utils/string.utils";

import { RelatedCasesModal } from "./RelatedCasesModal";
import { SelectedIdentifier } from "./useBlacklistIdentifiersForm";

export type BlacklistableIdentifiersTableProps = {
  blacklistableIdentifiers: BlacklistableCaseIdentifier[];
  alreadyListedIdentifiers: BlacklistedCaseIdentifier[];
  blacklistSchemas: ClientSchemaBlacklist[];
  selectedIdentifiers: SelectedIdentifier[];
  caseId: string;
  showValidationErrors: boolean;
  onToggle: (identifier: BlacklistableCaseIdentifier) => void;
  handleSelectAll: () => void;
  onSaveAdditionalProperties: (identifierId: string, values: PropertyValueInput[]) => void;
};

type ModalState =
  | { type: "complete"; identifier: BlacklistableCaseIdentifier }
  | { type: "edit"; identifier: BlacklistableCaseIdentifier }
  | { type: "relatedCases"; identifierId: string }
  | null;

export const BlacklistableIdentifiersList: FC<BlacklistableIdentifiersTableProps> = ({
  blacklistableIdentifiers,
  alreadyListedIdentifiers,
  blacklistSchemas,
  selectedIdentifiers,
  caseId,
  showValidationErrors,
  onToggle,
  handleSelectAll,
  onSaveAdditionalProperties,
}) => {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<ModalState>(null);
  const t = useT();

  const selectedIds = useMemo(
    () => new Set(selectedIdentifiers.map((s) => s.id)),
    [selectedIdentifiers]
  );

  const sortedIdentifiers = useMemo(
    () =>
      blacklistableIdentifiers.toSorted(
        (a, b) => Number(b.belongsToCurrentCase) - Number(a.belongsToCurrentCase)
      ),
    [blacklistableIdentifiers]
  );

  const filteredIdentifiers = useMemo(
    () => sortedIdentifiers.filter((identifier) => searchForIdentifier(search, identifier)),
    [search, sortedIdentifiers]
  );
  const getMissingMandatoryCount = (identifier: BlacklistableCaseIdentifier) => {
    const schema = blacklistSchemas.find((s) => s.code === identifier.blacklist.code);
    const mandatoryProps = schema?.properties.filter((p) => p.mandatory) ?? [];
    const selected = selectedIdentifiers.find((s) => s.id === identifier.id);
    // Source properties are auto-filled from the identifier value — never count as missing
    const sourceCodes = new Set(schema?.sourceProperties.map((p) => p.code) ?? []);
    return mandatoryProps.filter(
      (prop) =>
        !sourceCodes.has(prop.code) &&
        !selected?.additionalProperties.find((ap) => ap.code === prop.code && ap.value.trim())
    ).length;
  };

  const hasAdditionalProperties = (identifier: BlacklistableCaseIdentifier) => {
    const schema = blacklistSchemas.find((s) => s.code === identifier.blacklist.code);
    return (schema?.properties.length ?? 0) > 0;
  };

  const selectedMissingCount = blacklistableIdentifiers.filter(
    (id) => selectedIds.has(id.id) && getMissingMandatoryCount(id) > 0
  ).length;

  const getAdditionalProperties = (identifierId: string): PropertyValueInput[] =>
    selectedIdentifiers.find((s) => s.id === identifierId)?.additionalProperties ?? [];

  const buildItem = (
    identifier: BlacklistableCaseIdentifier,
    schema: ClientSchemaBlacklist
  ): BlacklistElementModalItem => {
    const properties: { property: Property; value: string }[] = schema.sourceProperties.map(
      (pr) => ({
        property: pr,
        value: identifier.identifier.value,
      })
    );

    return {
      id: identifier.id,
      sourceProperties: schema.sourceProperties.map((sp) => sp.code),
      properties,
    };
  };

  if (blacklistableIdentifiers.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center px-8 py-10 text-center">
        <CheckmarkFilled size={40} className="mb-4 text-supportSuccess" />
        <h4 className="text-base font-semibold">
          {t("alerts.confirmFraud.identifiers.empty.title")}
        </h4>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-5 text-textSecondary">
          {t("alerts.confirmFraud.identifiers.empty.description")}
        </p>
        {alreadyListedIdentifiers.length > 0 && (
          <div className="mx-auto mt-8 w-full max-w-2xl text-left">
            <div className="mb-3 text-center text-xs font-semibold text-textSecondary">
              {t("alerts.confirmFraud.identifiers.empty.alreadyListed.title")}
            </div>
            <p className="mb-4 text-center text-sm text-textSecondary">
              {t("alerts.confirmFraud.identifiers.empty.alreadyListed.description")}
            </p>
            <div className="flex flex-col gap-2.5">
              {alreadyListedIdentifiers.map((item, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 border border-solid border-borderSubtle01 bg-layer01 px-4 py-3 text-left text-sm"
                >
                  <CheckmarkFilled size={16} className="mt-0.5 shrink-0 text-supportSuccess" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-xs text-textSecondary">
                        {item.identifier.identifierType.name}
                      </span>
                      <span className="break-words font-medium text-textPrimary">
                        {item.identifier.value}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-textSecondary">
                      {t("alerts.confirmFraud.identifiers.empty.alreadyListed.blacklist")}{" "}
                      <span className="text-textPrimary">
                        {item.blacklistElement.blacklist.name}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full min-h-0 flex-col">
        <div className="border-b border-solid border-borderSubtle01 px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold">
                {t("alerts.confirmFraud.identifiers.table.title")}
              </h3>
              <p className="mt-1 text-sm text-textSecondary">
                {t("alerts.confirmFraud.identifiers.table.summary", {
                  selected: selectedIds.size,
                  missing: selectedMissingCount,
                })}
              </p>
            </div>
            <Search
              id="searchBlacklistableIdentifiers"
              size="md"
              className="w-full max-w-sm"
              labelText={t("alerts.confirmFraud.identifiers.table.search")}
              placeholder={t("alerts.confirmFraud.identifiers.table.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-5">
          {filteredIdentifiers.length === 0 ? (
            <div className="flex min-h-48 items-center justify-center bg-layer01 px-8 py-10 text-center">
              <p className="text-sm text-textSecondary">
                {t("alerts.confirmFraud.identifiers.table.search.empty")}
              </p>
            </div>
          ) : (
            <div>
              <div className="pl-4 pb-5">
                <Checkbox
                  id="select-all"
                  labelText={t("alerts.confirmFraud.identifiers.table.card.select.all")}
                  aria-label="select all"
                  checked={selectedIdentifiers.length == blacklistableIdentifiers.length}
                  onChange={() => handleSelectAll()}
                />
              </div>
              {filteredIdentifiers.map((identifier) => {
                const selected = selectedIds.has(identifier.id);
                const missingCount = getMissingMandatoryCount(identifier);
                const hasRequiredMissing = selected && missingCount > 0;
                const hasProperties = hasAdditionalProperties(identifier);
                const hasExistingValues = getAdditionalProperties(identifier.id).length > 0;
                const invalid = showValidationErrors && hasRequiredMissing;
                const linkedCaseCount = identifier.linkedCases.length;

                return (
                  <div
                    key={identifier.id}
                    className={cn(
                      "grid grid-cols-[auto_1fr_auto] items-start gap-4 border border-solid border-borderSubtle01 bg-layer01 p-4",
                      { "outline outline-2 -outline-offset-2 outline-supportError": invalid }
                    )}
                  >
                    <Checkbox
                      id={`select-${identifier.id}`}
                      labelText=""
                      aria-label={t("alerts.confirmFraud.identifiers.table.card.select", {
                        identifier: identifier.identifier.value,
                      })}
                      checked={selected}
                      onChange={() => onToggle(identifier)}
                    />

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs text-textSecondary">
                            {identifier.identifier.identifierType.name}
                          </div>
                          <div className="mt-1 break-words text-base font-semibold">
                            {identifier.identifier.value}
                          </div>
                        </div>
                        {hasRequiredMissing && (
                          <Tag type="gray" renderIcon={WarningFilled}>
                            {t("alerts.confirmFraud.identifiers.table.completion.missing")}
                          </Tag>
                        )}
                      </div>

                      <div className="mt-4 grid grid-cols-[minmax(12rem,16rem)_minmax(0,1fr)] gap-4 text-sm">
                        <div>
                          <div className="text-xs text-textSecondary">
                            {t("alerts.confirmFraud.identifiers.table.blacklist.column.title")}
                          </div>
                          <div className="mt-1">{identifier.blacklist.name}</div>
                        </div>
                        <div>
                          <div className="text-xs text-textSecondary">
                            {t("alerts.confirmFraud.identifiers.table.currentCase.column.title")}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-2 text-textSecondary">
                            {identifier.belongsToCurrentCase && (
                              <span className="inline-flex h-5 items-center gap-1 border border-solid border-borderSubtle01 px-1 text-xs font-medium leading-none text-textPrimary">
                                <CheckmarkFilled size={12} className="text-supportSuccessInverse" />
                                {t(
                                  "alerts.confirmFraud.identifiers.table.currentCase.column.value"
                                )}
                              </span>
                            )}
                            {linkedCaseCount > 0 && (
                              <button
                                type="button"
                                className="inline-flex h-5 cursor-pointer items-center gap-1 border border-solid border-borderSubtle01 bg-transparent px-1 text-xs font-medium leading-none text-textPrimary hover:border-linkPrimary hover:bg-layerHover01 hover:text-linkPrimary focus:outline focus:outline-2 focus:outline-focus"
                                onClick={() =>
                                  setModal({ type: "relatedCases", identifierId: identifier.id })
                                }
                              >
                                +{linkedCaseCount}{" "}
                                {t(
                                  "alerts.confirmFraud.identifiers.table.relatedCases.column.title"
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex min-w-32 justify-end">
                      {hasProperties && selected && (
                        <Button
                          kind="ghost"
                          size="sm"
                          renderIcon={Edit}
                          onClick={() =>
                            setModal({
                              type: hasExistingValues || !hasRequiredMissing ? "edit" : "complete",
                              identifier,
                            })
                          }
                        >
                          {hasRequiredMissing
                            ? t("alerts.confirmFraud.identifiers.table.details.complete")
                            : t("alerts.confirmFraud.identifiers.table.details.edit")}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ModalTransition>
        {modal?.type === "complete" &&
          (() => {
            const schema = blacklistSchemas.find((s) => s.code === modal.identifier.blacklist.code);
            if (!schema) return null;
            const fakeItem = buildItem(modal.identifier, schema);
            return (
              <BlacklistElementModal
                onClose={() => setModal(null)}
                onSuccess={() => setModal(null)}
                item={fakeItem}
                listSchema={schema}
                showOnlyMandatory
                onSave={(values) => {
                  onSaveAdditionalProperties(modal.identifier.id, values);
                  setModal(null);
                }}
                title={t("alerts.confirmFraud.identifiers.details.modal.title")}
                subtitle={`${modal.identifier.identifier.identifierType.name} - ${modal.identifier.identifier.value}`}
              />
            );
          })()}
      </ModalTransition>

      <ModalTransition>
        {modal?.type === "edit" &&
          (() => {
            const schema = blacklistSchemas.find((s) => s.code === modal.identifier.blacklist.code);
            if (!schema) return null;
            const fakeItem = buildItem(modal.identifier, schema);
            return (
              <BlacklistElementModal
                onClose={() => setModal(null)}
                onSuccess={() => setModal(null)}
                item={fakeItem}
                listSchema={schema}
                onSave={(values) => {
                  onSaveAdditionalProperties(modal.identifier.id, values);
                  setModal(null);
                }}
                title={t("alerts.confirmFraud.identifiers.details.modal.title")}
                subtitle={`${modal.identifier.identifier.identifierType.name} - ${modal.identifier.identifier.value}`}
              />
            );
          })()}
      </ModalTransition>

      <ModalTransition>
        {modal?.type === "relatedCases" && (
          <RelatedCasesModal
            onClose={() => setModal(null)}
            caseId={caseId}
            identifierId={modal.identifierId}
          />
        )}
      </ModalTransition>
    </>
  );
};

const searchForIdentifier = (search: string, identifier: BlacklistableCaseIdentifier) => {
  if (!search.trim()) return true;
  return (
    searchInString(search, identifier.identifier.identifierType.name) ||
    searchInString(search, identifier.identifier.value) ||
    searchInString(search, identifier.element.name) ||
    searchInString(search, identifier.blacklist.name)
  );
};

const searchInString = (search: string, value: string) =>
  value.toLowerCase().includes(search.toLowerCase());
